import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { getBaseApiUrl, getCorsSettings } from '../utils/apiConfig';

// Create context
const PublicSettingsContext = createContext();

// API configuration
const API_URL = getBaseApiUrl();
const API_TIMEOUT = parseInt(import.meta.env.VITE_API_TIMEOUT || '10000', 10);
const corsSettings = getCorsSettings();

// Create axios instance
const apiClient = axios.create({
  baseURL: API_URL,
  timeout: API_TIMEOUT,
  withCredentials: true, // Enable cookies for refresh token
  ...corsSettings,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for authentication
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Cache configuration
const CACHE_KEY = 'jetogo_public_settings';

// Helper functions for cache management
const getCachedSettings = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const { data, version } = JSON.parse(cached);

    // Return cached data with version
    return { data, version };
  } catch (error) {
    console.warn('Failed to read cached settings:', error);
    return null;
  }
};

const setCachedSettings = (data, version) => {
  try {
    const cacheData = {
      data,
      version,
      cachedAt: Date.now() // For debugging
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
  } catch (error) {
    console.warn('Failed to cache settings:', error);
  }
};

const checkSettingsVersion = async () => {
  try {
    const response = await apiClient.get('/public/settings-version');
    return response.data?.version || 1;
  } catch (error) {
    console.warn('Failed to check settings version:', error);
    return null;
  }
};

// Provider component
export const PublicSettingsProvider = ({ children }) => {
  // Check cache first
  const cache = getCachedSettings();
  const cachedData = cache?.data;

  // Initialize state with cached values or defaults
  const [countryCount, setCountryCount] = useState(cachedData?.country_count || 135);
  const [premiumPrice, setPremiumPrice] = useState(cachedData?.premium_price || 29.99);
  const [soloPrice, setSoloPrice] = useState(cachedData?.solo_price || 19.99);
  const [siteName, setSiteName] = useState(cachedData?.site_name || 'Jetogo');
  const [ogTitle, setOgTitle] = useState(cachedData?.og_title || 'Jetogo - Global eSIM for Travelers');
  const [ogDescription, setOgDescription] = useState(cachedData?.og_description || 'Keep one eSIM for life. Your Jetogo credit never expires — just top up and connect in 135+ countries. No contracts, no monthly fees, no wasted data.');
  const [ogImage, setOgImage] = useState(cachedData?.og_image || '');
  const [ogUrl, setOgUrl] = useState(cachedData?.og_url || import.meta.env.VITE_SITE_URL || 'https://jetogo.com');
  const [ogType, setOgType] = useState(cachedData?.og_type || 'website');
  const [twitterCard, setTwitterCard] = useState(cachedData?.twitter_card || 'summary_large_image');
  const [twitterImage, setTwitterImage] = useState(cachedData?.twitter_image || '');
  const [trustpilotInvitesEnabled, setTrustpilotInvitesEnabled] = useState(cachedData?.trustpilot_invites_enabled || false);
  const [loading, setLoading] = useState(false); // We'll check version first

  useEffect(() => {
    const initializeSettings = async () => {
      // Step 1: Check current version from API (lightweight)
      const currentVersion = await checkSettingsVersion();

      // Step 2: Compare with cached version
      if (cache && currentVersion && cache.version === currentVersion) {
        console.log(`✅ Using cached settings (version ${currentVersion})`);
        return; // Use cached data, no need to fetch
      }

      // Step 3: Version mismatch or no cache - fetch fresh data
      if (currentVersion) {
        console.log(`🔄 Settings version changed (${cache?.version || 'none'} → ${currentVersion}), fetching fresh data...`);
      } else {
        console.log('⚠️ Could not check version, fetching settings...');
      }

      setLoading(true);
      await fetchPublicSettings(currentVersion);
      setLoading(false);
    };

    const fetchPublicSettings = async (version) => {
      try {
        const settingsToFetch = [
          'country_count', 'site_name', 'og_title', 'og_description',
          'og_image', 'og_url', 'og_type', 'twitter_card', 'twitter_image',
          'trustpilot_invites_enabled'
        ];

        // Single batch request instead of 10 individual requests
        const response = await apiClient.get(
          `/public/system-settings?keys=${settingsToFetch.join(',')}`
        );
        const batchData = response.data?.data || {};

        // Use default plan prices (admin endpoint requires authentication)
        const planPrices = { premium_price: 29.99, solo_price: 19.99 };

        // Build settings object for cache
        const settingsData = {
          premium_price: planPrices.premium_price,
          solo_price: planPrices.solo_price,
          ...batchData
        };

        // Update state with plan prices
        setPremiumPrice(planPrices.premium_price);
        setSoloPrice(planPrices.solo_price);

        // Update state with fetched settings values
        const setters = {
          country_count: setCountryCount,
          site_name: setSiteName,
          og_title: setOgTitle,
          og_description: setOgDescription,
          og_image: setOgImage,
          og_url: setOgUrl,
          og_type: setOgType,
          twitter_card: setTwitterCard,
          twitter_image: setTwitterImage,
          trustpilot_invites_enabled: setTrustpilotInvitesEnabled,
        };

        for (const [key, value] of Object.entries(batchData)) {
          if (value !== null && setters[key]) {
            setters[key](value);
          }
        }

        // Cache the settings with version
        setCachedSettings(settingsData, version || 1);
      } catch (error) {
        console.warn('Failed to fetch public settings, using defaults:', error);
      }
    };

    initializeSettings();
  }, []);

  return (
    <PublicSettingsContext.Provider value={{
      countryCount,
      premiumPrice,
      soloPrice,
      siteName,
      ogTitle,
      ogDescription,
      ogImage,
      ogUrl,
      ogType,
      twitterCard,
      twitterImage,
      trustpilotInvitesEnabled,
      loading
    }}>
      {children}
    </PublicSettingsContext.Provider>
  );
};

// Custom hook to use the context
export const usePublicSettings = () => {
  const context = useContext(PublicSettingsContext);
  if (context === undefined) {
    throw new Error('usePublicSettings must be used within a PublicSettingsProvider');
  }
  return context;
};
